import React, {createContext, Dispatch, PropsWithChildren, useContext, useEffect, useMemo, useReducer} from "react";
import {
  IAvailableFilter,
  IItemContent,
  IItemSearchResponse,
  IQueryData,
  ProductList,
  ProductListFilterItemRangeValue,
  ProductListFilterItemType,
  ProductListFilterItemValueType,
  ProductListOrder,
  ProductListOrderBy,
} from "../../../typescript-library";
import window from "../../lib/WindowExtended";
import {mapUrlParamsToFilterState} from "./helpers/mapUrlParamsToFilterState";
import {reducer} from "./reducer";
import {ProductListAction} from "./actions";
import {convertToFilterTypes} from "./helpers/convertToFilterTypes";
import {useMediaQuery} from "react-responsive";
import {ProductListProps} from "../productList/ProductList";
import {useNonInitialEffect} from "../../../helpers/useNonInitialEffect";
import {getItemsWithPercentDiscount, getRequestParams} from "./helpers/percentDiscountHelper";
import {searchListFetch, searchListRequestParams} from "./helpers/searchListFetch";
import {getFromDatabase, setToDatabase, swapToCurrent} from "../../../storage/dbAdapter";

export type ProductListState = {
  instance?: ProductList;
  offset: number;
  limit: number;
  orderBy?: ProductListOrderBy;
  order?: ProductListOrder;
  loading: boolean;
  items: IItemContent[];
  /* All filters that are engaged */
  filters: {[k: string]: ProductListFilterItemValueType};
  total: number;
  isLastPage: boolean;
  showFilters: boolean;
  trigger?: boolean;
  /* Map of range filters, that are engaged */
  rangeFilterMap?: Map<string, ProductListFilterItemRangeValue>;
  filtersToShow: string[];
  filtersToHide: string[];
  defaultFilter?: string;
};

export const initialState: ProductListState = {
  offset: 0,
  limit: ProductList.PRODUCT_LIST_DEFAULT_LIMIT,
  loading: false,
  items: [],
  filters: {},
  total: 0,
  isLastPage: true,
  showFilters: false,
  rangeFilterMap: new Map(),
  filtersToShow: [],
  filtersToHide: [],
  defaultFilter: undefined,
};

interface IProductListContext {
  state: ProductListState;
  dispatch: Dispatch<ProductListAction>;
}

export const ProductListContext = createContext<IProductListContext>({
  state: initialState,
  dispatch: () => initialState,
});

export function useProductListContext(): IProductListContext {
  return useContext<IProductListContext>(ProductListContext);
}

export const isSearchPage = document.body.id === "C100013";
export let preloadItems: IItemSearchResponse | null = null;

const ProductListProvider: React.FC<PropsWithChildren<ProductListProps>> = ({
  children,
  shopId,
  categoryId,
  customerGroupId,
  customerId,
  currency = "USD",
  token,
  filtersToHide,
  filtersToShow,
  defaultFilter,
}) => {
  const isMobile = useMediaQuery({query: "(max-width: 940px)"});

  // 24 products are displayed on mobile devices and 48 on desktop
  ProductList.PRODUCT_LIST_DEFAULT_LIMIT = isMobile ? 24 : 48;

  const firstState: ProductListState = useMemo((): ProductListState => {
    const stateByUrlParams = mapUrlParamsToFilterState(initialState).state;
    // Default order by newest products for design-neuheiten.html
    const isNewProductsSite = document.body.id === "C104719";

    return {
      ...stateByUrlParams,
      instance: new ProductList(
        shopId,
        categoryId,
        currency,
        undefined,
        customerId ?? undefined,
        customerGroupId ?? undefined,
      ),
      ...(isNewProductsSite && !stateByUrlParams.orderBy
        ? {orderBy: "newest" as ProductListOrderBy, order: "desc" as ProductListOrder}
        : {}),
      filtersToShow,
      filtersToHide,
      defaultFilter,
    };
  }, []);

  const [state, dispatch] = useReducer(reducer, firstState);

  useNonInitialEffect(() => {
    dispatch({
      type: "RESET",
    });
  }, [currency, customerId, customerGroupId]);

  useEffect(() => {
    const {instance} = state;
    if (!instance) {
      return;
    }
    const onPopState = () => {
      const {state: mappedState, filters: mappedFilters} = mapUrlParamsToFilterState(state);
      const {limit, order, orderBy} = mappedState;
      dispatch({type: "SET_SORT", payload: {orderBy, order}});
      dispatch({
        type: "SET_FILTERS",
        payload: {filters: convertToFilterTypes(mappedFilters, instance.filter.filterItems)},
      });
      dispatch({type: "TRIGGER"});
      dispatch({type: "SET_LIMIT", payload: limit});
    };

    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [state.instance]);

  const initialRangeFilters = (filtersFromResponse: IAvailableFilter[]) => {
    const {instance} = state;

    if (!instance) {
      return;
    }
    /* 'field' key string array of any set range filter */
    const anySetRangeFilters = Array.from(instance.filter.filterItems).reduce((acc: string[], [key, keyValue]) => {
      if (keyValue.type === ProductListFilterItemType.range && state.filters[key] !== undefined) {
        acc.push(key);
      }

      return acc;
    }, []);

    if (anySetRangeFilters.length) {
      return;
    }

    filtersFromResponse.forEach((filter) => {
      if (filter.type === ProductListFilterItemType.range)
        dispatch({
          type: "SET_RANGE_FILTERS_VALUES",
          payload: {
            field: filter.field,
            value: {
              min: Math.floor(Number(filter.minValue ?? 0)),
              max: Math.ceil(Number(filter.maxValue ?? 0)),
            },
          },
        });
    });
  };

  /**
   * Generally this is done in the tslib `ProductList`, but since we need to hack around the filter
   * handling, we just do it here after a call on the `ItemFactory`, or the search list fetch
   * @param result
   * @param instance
   */
  const applyToInstance = (result: IItemSearchResponse, instance: ProductList): void => {
    result.availableFilters = result.availableFilters.filter((af) => !!af.values);
    instance.filter.applyFilterResult(result.availableFilters);
    instance.items = result.itemContent;
    instance.total = result.totalSearchHits;
  };

  const fetchItems = async (instance: ProductList): Promise<IItemSearchResponse> => {
    let queryData: IQueryData = {};
    let urlSearchParams: URLSearchParams;

    if (isSearchPage) {
      urlSearchParams = searchListRequestParams(instance);
    } else {
      queryData = getRequestParams(instance, state.filters);
      urlSearchParams = new URLSearchParams(queryData);
    }

    const offset = urlSearchParams.get("offset") || "0";
    let response: IItemSearchResponse | undefined;
    if (offset === "0") {
      // Page load
      // Any database entry, holding first items and preload?
      const tempResponse = await getFromDatabase(urlSearchParams);

      if (tempResponse?.current) {
        applyToInstance(tempResponse.current, instance);
        if (tempResponse.preload) {
          preloadItems = tempResponse.preload;
        }

        return tempResponse.current;
      }

      // on offset 0/page load, fetch the visible and the preload items all at once
      const visibleLimit = Number(urlSearchParams.get("limit") || "0");
      if (isSearchPage) {
        const tempUrlSearchParams = new URLSearchParams(urlSearchParams);
        tempUrlSearchParams.set("limit", `${visibleLimit + ProductList.PRODUCT_LIST_DEFAULT_LIMIT}`);
        response = await searchListFetch(instance, tempUrlSearchParams, token);
      } else {
        const tempQueryData = {...queryData, limit: visibleLimit + ProductList.PRODUCT_LIST_DEFAULT_LIMIT};
        response = await getItemsWithPercentDiscount(tempQueryData);
      }

      const responseForPreload = {...response};
      // Splice all items from the visible limit on to the preload, keep the rest in the original response
      responseForPreload.itemContent = response.itemContent.splice(visibleLimit);
      preloadItems = responseForPreload;

      // Cache only if the items array is not empty
      // Temporary measure!! until our search provider is stable, this check is only needed when offset=0
      if (response.itemContent.length) {
        void setToDatabase({current: response, preload: responseForPreload}, urlSearchParams);
      }
    } else {
      // All other calls, by "View more". Should be called solely by _preload_
      response = await (isSearchPage
        ? searchListFetch(instance, urlSearchParams, token)
        : getItemsWithPercentDiscount(queryData));
      void setToDatabase({preload: response}, urlSearchParams);
    }

    applyToInstance(response, instance);

    return response;
  };

  const handleResponse = (data: IItemSearchResponse) => {
    const {instance} = state;
    if (!instance) {
      return;
    }

    initialRangeFilters(data.availableFilters);

    // check if first load and if search param filters contains state filters
    const {filters: paramsFilters} = mapUrlParamsToFilterState(state);

    // Mostly relevant after the first call. Set defaultFilters as soon as available filters are known
    // Will be hidden from the browser url in `updateProductListRequestParams.ts`
    // Will be hidden from Filter View in `ProductListFilter.filterItemArray()`
    // and from filter overview bar in `SelectedFilterOverview.filtersEntries` const
    if (defaultFilter) {
      const [key, value] = defaultFilter.split("=");
      paramsFilters[key] = value;
    }

    let totalItemCount = data.totalSearchHits;

    if (Object.keys(paramsFilters).some((filterKey) => !state.filters[filterKey])) {
      const filters = convertToFilterTypes(paramsFilters, instance.filter.filterItems);
      dispatch({type: "SET_FILTERS", payload: {filters}});
    } else {
      let items: IItemContent[];
      if (state.offset === 0) {
        items = data.itemContent;
        // if the default sorting (rank) is active and there are no filters set, prepend manualTopItems if they exist
        if (
          (state.orderBy === undefined || state.orderBy === "rank") &&
          Object.keys(state.filters).length === 0 &&
          data.manualTopItems
        ) {
          items = [...data.manualTopItems, ...items];
          totalItemCount += data.manualTopItems.length;
        }
      } else {
        items = [...state.items, ...data.itemContent];
      }
      dispatch({type: "SET_ITEMS", payload: {items, total: totalItemCount}});
      instance.filter.applyFilterValue(state.filters);
      // When offset === 0, it's already done in the fetItems() on page load
      if (items.length < data.totalSearchHits && state.offset !== 0) void preLoading(data);
    }
  };

  const preLoading = async (data: IItemSearchResponse) => {
    const instance = state.instance;
    if (!instance) {
      return;
    }
    if (Object.keys(mapUrlParamsToFilterState(state).filters).some((key) => !state.filters[key])) {
      return;
    }

    preloadItems = null;
    const oldOffset = instance.offset;
    const oldLimit = instance.limit;
    instance.offset = state.offset + state.limit;
    instance.limit = ProductList.PRODUCT_LIST_DEFAULT_LIMIT;

    const tempResponse = await fetchItems(instance);

    preloadItems = {
      ...data,
      itemContent: tempResponse.itemContent,
    };
    if (preloadItems.itemContent.length === 0) {
      // On a search list the further loads could possibly result in 0 items
      dispatch({type: "SET_IS_LAST_PAGE_BY_PRELOAD"});
    }
    instance.offset = oldOffset;
    instance.limit = oldLimit;
  };

  /**
   * ===================  Preparation of instance  ==================
   */
  const prepareRequest = () => {
    const {instance} = state;
    if (!instance) {
      return;
    }

    const oldOffset = instance.offset;
    instance.offset = state.offset;
    instance.limit = state.limit;
    instance.filter.applyFilterValue(state.filters);
    instance.orderBy = state.orderBy;
    instance.order = state.order;

    if (oldOffset < instance.offset && !!preloadItems) {
      const currentRequestParams = isSearchPage
        ? searchListRequestParams(instance)
        : new URLSearchParams(getRequestParams(instance, state.filters));
      void swapToCurrent(instance, currentRequestParams);
      handleResponse(preloadItems);

      return;
    }

    if (isSearchPage && !new URLSearchParams(window.location.search).get("q")) {
      // Customer should see a message telling to enter a search term
      return;
    }

    dispatch({
      type: "SET_LOADING",
      payload: true,
    });

    fetchItems(instance)
      .then(handleResponse)
      .finally(() => {
        dispatch({
          type: "SET_LOADING",
          payload: false,
        });
      });
  };

  useEffect(prepareRequest, [JSON.stringify(state.filters), state.orderBy, state.order, state.offset, state.limit]);

  return <ProductListContext.Provider value={{state, dispatch}}>{children}</ProductListContext.Provider>;
};

export default ProductListProvider;
